import { useCallback, useEffect, useLayoutEffect, useRef, type Ref, type TextareaHTMLAttributes } from "react";

/**
 * The site's one multi-line text field. It starts at its minimum height (its
 * `rows`, or the CSS `min-height`), grows with the text as the author types —
 * or when the text is set programmatically (an AI rewrite, a restored draft) —
 * and stops at the CSS `max-height`, past which it scrolls. There is no resize
 * handle: the size always follows the content. Every textarea element in the app
 * goes through this component (textareaUsage.test.ts enforces it).
 */
export function AutoTextarea({
  ref,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = useCallback(
    (element: HTMLTextAreaElement | null) => {
      innerRef.current = element;
      if (typeof ref === "function") ref(element);
      else if (ref) ref.current = element;
    },
    [ref]
  );

  // Re-fit whenever the text changes, however it changed.
  useLayoutEffect(() => {
    if (innerRef.current) fitToContent(innerRef.current);
  }, [props.value]);

  // A width change re-wraps the text, so the height needed changes too.
  useEffect(() => {
    const element = innerRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    let lastWidth = element.clientWidth;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === lastWidth) return;
      lastWidth = element.clientWidth;
      fitToContent(element);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <textarea
      {...props}
      ref={setRefs}
      onInput={(event) => {
        // Uncontrolled fields (defaultValue) have no value prop to watch.
        fitToContent(event.currentTarget);
        props.onInput?.(event);
      }}
    />
  );
}

/**
 * Sizes `element` to its content, clamped to its CSS `max-height`; shows the
 * scrollbar only when the content is taller than that. The CSS `min-height`
 * (or `rows`) sets the floor on its own, since `height: auto` resolves to it.
 *
 * Measuring needs a momentary collapse to `height: auto`, which shrinks every
 * scroll container around the field — the browser then clamps their scroll
 * positions and the view would jump on each keystroke. So the page's, each
 * scrolled ancestor's and the field's own scroll offsets are put back after.
 */
function fitToContent(element: HTMLTextAreaElement): void {
  const scrolled: Array<[Element, number]> = [];
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (node.scrollTop > 0) scrolled.push([node, node.scrollTop]);
  }
  const pageY = window.scrollY;
  const ownTop = element.scrollTop;

  element.style.height = "auto";
  const style = window.getComputedStyle(element);
  const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth) || 0;
  const needed = element.scrollHeight + borders;
  const max = parseFloat(style.maxHeight);
  const height = Number.isFinite(max) && max > 0 ? Math.min(needed, max) : needed;
  element.style.height = `${height}px`;
  element.style.overflowY = needed > height ? "auto" : "hidden";

  for (const [node, top] of scrolled) node.scrollTop = top;
  if (window.scrollY !== pageY) window.scrollTo(window.scrollX, pageY);
  element.scrollTop = ownTop;
}
